// Service layer — the integration seam between presentational components and
// the backend BFF (or mocks).
//
// Each domain has an interface, a mock implementation, and an HTTP
// implementation. The factory at the bottom returns one or the other based on
// env.useMocks. Components NEVER import mocks or fetch directly — they call
// services.*, so swapping the data source is a one-line env change.

import { env } from "../config/env";
import { http, mockResolve } from "./client";
import * as fx from "../mocks/fixtures";
import { ApiError } from "../types";

// Mutable mock state so save/remove/invite/update persist within a session.
let mockProviders: ProviderRow[] = structuredClone(fx.PROVIDERS);
let mockTeam: TeamMember[] = structuredClone(fx.TEAM_MEMBERS);
const mockTierState: Record<"music" | "image" | "video", Record<string, string>> = {
  music: {},
  image: {},
  video: {},
};

const MOCK_TIER_DEFAULTS: Record<"music" | "image" | "video", Record<string, string>> = {
  music: { amateur: "musicgen", pro: "suno-v4", studio: "suno-v5-5", legend: "best-of" },
  image: { amateur: "flux-schnell", pro: "flux-dev", studio: "reve", legend: "nano-banana-pro" },
  video: { amateur: "wan-480p", pro: "kling", studio: "happyhorse-i2v", legend: "seedance-pro" },
};

const MOCK_TIER_MODELS: Record<"music" | "image" | "video", { id: string; label: string; credits: number }[]> = {
  music: [
    { id: "musicgen", label: "MusicGen (Replicate)", credits: 3 },
    { id: "suno-v4", label: "Suno v4", credits: 5 },
    { id: "suno-v4-5", label: "Suno v4.5", credits: 6 },
    { id: "suno-v5", label: "Suno v5", credits: 8 },
    { id: "suno-v5-5", label: "Suno v5.5", credits: 10 },
    { id: "best-of", label: "Best-of (Suno + Lyria)", credits: 18 },
    { id: "lyria-pro", label: "Lyria 3 Pro", credits: 10 },
    { id: "lyria-clip", label: "Lyria 3 Clip", credits: 4 },
  ],
  image: [
    { id: "flux-schnell", label: "Flux Schnell (Replicate)", credits: 1 },
    { id: "flux-dev", label: "Flux Dev (Replicate)", credits: 3 },
    { id: "sdxl", label: "SDXL (Replicate)", credits: 2 },
    { id: "reve", label: "Reve", credits: 3 },
    { id: "nano-banana-pro", label: "Nano Banana Pro (Gemini)", credits: 2 },
    { id: "gpt-image-2", label: "GPT Image 2 (OpenAI)", credits: 4 },
    { id: "dall-e-3", label: "DALL·E 3 (OpenAI)", credits: 4 },
  ],
  video: [
    { id: "wan-480p", label: "Wan 2.1 480p (WaveSpeed)", credits: 8 },
    { id: "wan-720p", label: "Wan 2.1 720p (WaveSpeed)", credits: 15 },
    { id: "kling", label: "Kling 1.6 Standard (Replicate)", credits: 15 },
    { id: "kling-pro", label: "Kling 1.6 Pro (Replicate)", credits: 25 },
    { id: "seedance-lite", label: "Seedance 1 Lite (ByteDance)", credits: 20 },
    { id: "seedance-pro", label: "Seedance 1 Pro (ByteDance)", credits: 40 },
    { id: "happyhorse-t2v", label: "Happy Horse 1.0 (text-to-video)", credits: 25 },
    { id: "happyhorse-i2v", label: "Happy Horse 1.0 (image-to-video)", credits: 25 },
    { id: "happyhorse-r2v", label: "Happy Horse 1.0 (reference-to-video)", credits: 30 },
    { id: "veo-3-1-gemini", label: "Veo 3.1 (Gemini direct)", credits: 50 },
  ],
};

function mockTierMappings(): TierMappingsView {
  const TIER_ORDER: ("amateur" | "pro" | "studio" | "legend")[] = ["amateur", "pro", "studio", "legend"];
  const cells: TierMappingCell[] = [];
  (["music", "image", "video"] as const).forEach((kind) => {
    const defaults = MOCK_TIER_DEFAULTS[kind];
    const overrides = mockTierState[kind];
    TIER_ORDER.forEach((tier) => {
      const defaultModelId = defaults[tier] ?? "";
      const overrideId = overrides[tier];
      const modelId = overrideId ?? defaultModelId;
      if (!modelId) return;
      const meta = MOCK_TIER_MODELS[kind].find((m) => m.id === modelId);
      cells.push({
        kind,
        tier,
        modelId,
        modelLabel: meta?.label ?? modelId,
        creditsPerGeneration: meta?.credits ?? 0,
        isDefault: !overrideId || overrideId === defaultModelId,
        defaultModelId,
      });
    });
  });
  return {
    cells,
    availableModels: {
      music: MOCK_TIER_MODELS.music.map((m) => ({ id: m.id, label: m.label, kind: "music", creditsPerGeneration: m.credits })),
      image: MOCK_TIER_MODELS.image.map((m) => ({ id: m.id, label: m.label, kind: "image", creditsPerGeneration: m.credits })),
      video: MOCK_TIER_MODELS.video.map((m) => ({ id: m.id, label: m.label, kind: "video", creditsPerGeneration: m.credits })),
    },
  };
}
import type {
  AuditEvent,
  BundleRequest,
  Channel,
  ChannelConnectResponse,
  Command,
  CreditBalance,
  DashboardData,
  GenerateBatchRequest,
  GenerateBatchResponse,
  GenerateConfig,
  GenerateRequest,
  GenerateResponse,
  InviteResult,
  LibraryItem,
  ModelOption,
  Plan,
  RenderBrief,
  PlatformCloudflareWrite,
  PlatformGithubWrite,
  PlatformJobDetail,
  PlatformJobRow,
  PlatformOAuthProviderState,
  PlatformOriginsWrite,
  PlatformQuery,
  PlatformSettings,
  PlatformSmtpWrite,
  PlatformStats,
  PlatformStripeWrite,
  PlatformTenantDetail,
  PlatformTenantLookup,
  PlatformTenantPatch,
  PlatformTenantRow,
  PlatformUserDetail,
  PlatformUserPatch,
  PlatformUserRow,
  ProviderRow,
  PublishRequest,
  PublishState,
  RenderActionResponse,
  RenderDetail,
  RenderJob,
  RenderTab,
  Role,
  SaveProviderInput,
  TeamMember,
  TestResult,
  TierMappingCell,
  TierMappingsView,
  TrackAnalysis,
  User,
  WebhookRow,
} from "../types";

/* ---------- Interfaces ---------- */
export interface DashboardService {
  getDashboard(): Promise<DashboardData>;
}
export interface LibraryService {
  list(opts?: { type?: string; q?: string }): Promise<LibraryItem[]>;
  get(id: string): Promise<LibraryItem | undefined>;
  publish(id: string, body: PublishRequest): Promise<PublishState>;
  delete(id: string): Promise<void>;
  /** Delete N items in one request. Returns which ids actually went
   *  through and which the server refused (typically: not owned, not
   *  found). Replaces the SPA's per-id DELETE loop. */
  bulkDelete(ids: string[]): Promise<{ deleted: string[]; failed: string[] }>;
  // Link an existing image render as the cover thumbnail for a music
  // item. Returns the resulting cover URL so the SPA can update the
  // card without a full re-fetch.
  setCoverLink(
    musicId: string,
    imageRenderId: string,
  ): Promise<{ coverImageUrl: string; imageRenderId: string }>;
  clearCoverLink(musicId: string): Promise<void>;
}
export interface RendersService {
  list(tab: RenderTab): Promise<RenderJob[]>;
  counts(): Promise<Record<RenderTab, number>>;
  detail(id: string): Promise<RenderDetail>;
  download(id: string): Promise<RenderActionResponse>;
  regenerate(
    id: string,
    overrides?: { lyrics?: string; title?: string },
  ): Promise<RenderActionResponse>;
  /**
   * Run Claude's Art Director against a completed music render and
   * return a cover-art image prompt. Used by the Image lane to pre-
   * fill its prompt field after the user approves the music.
   */
  /**
   * Optional setup hints from the RemixSetupPanel. The backend builds the
   * cover prompt from these + the track's measured analysis when the render
   * has no orchestrator brief (uploaded / picked / remixed tracks), so the
   * Image lane's auto-prompt works for brief-less sources too. Missing
   * fields are omitted; an empty/absent body is the original behaviour.
   */
  artPrompt(
    id: string,
    setup?: { genre?: string; mood?: string; title?: string },
  ): Promise<{
    artworkPrompt: string;
    composition?: string;
    palette: string[];
    lighting?: string;
    aspectRatioHint?: string;
    references: string[];
  }>;
  /**
   * Cinematographer's motion-direction prompt for this music render.
   * Used by the "Generate video" affordance on music cards so the
   * camera moves stay consistent with the song's concept + cover.
   */
  motionPrompt(
    id: string,
    options?: { targetEngine?: string },
  ): Promise<{
    motionPrompt: string;
    cameraMoves: string[];
    pacing?: string;
    transitions?: string;
    focalSubject?: string;
  }>;
  /**
   * Translate this render's lyrics into ``targetLanguage`` via Claude's
   * Lyricist persona. Preserves section markers, line breaks, and
   * approximate syllable count. Doesn't queue a new render; the
   * caller takes the returned body and submits via generate.submit
   * with prebuiltBrief.
   */
  translateLyrics(
    id: string,
    targetLanguage: string,
  ): Promise<{
    body: string;
    notes?: string;
    targetLanguage: string;
    sourceLanguage?: string;
  }>;
  publish(id: string, body: PublishRequest): Promise<PublishState>;
  /**
   * Analyze a SET track (POST /tracks/{jobId}/analyze). bpm / key /
   * durationS are MEASURED with librosa; genre / mood are an optional
   * AI SUGGESTION (either may be null — then the user types them). The
   * first call can take ~1-3s (librosa); the BFF caches afterwards
   * (``cached: true`` on a hit). Returns ``null`` (never throws) on any
   * failure so the remix-setup panel degrades gracefully — the flow is
   * never blocked on analysis.
   */
  analyzeTrack(jobId: string): Promise<TrackAnalysis | null>;
  /** Open an SSE connection for progress updates. */
  streamUrl(): string;
  /**
   * Mint a short-TTL signed URL the browser can paste straight into
   * an <audio>/<video> ``src`` — no credentialed cross-origin fetch,
   * no CORS preflight, no session-expiry mid-play, no iPad-Safari
   * ITP exposure. Token TTL ~1 hour; the BFF returns ``expiresIn``
   * so the caller can re-sign before playback runs out.
   */
  signUrl(id: string): Promise<{ url: string; expiresIn: number }>;
}
export interface ChannelsService {
  list(): Promise<Channel[]>;
  connect(platform: string, handle?: string): Promise<ChannelConnectResponse>;
  /** Start a real OAuth handshake for a platform. Returns the authorize URL. */
  connectStart(platform: "youtube" | "tiktok" | "instagram" | "soundcloud"): Promise<{ authorizeUrl: string; state: string }>;
  /** Disconnect a stored channel by id. */
  disconnect(id: string): Promise<void>;
}
/** Response of POST /embed/mv-token — everything the MV embed iframe's
 *  init message needs: the partner-signed token, where to point the
 *  iframe, and (when a musicRenderId was sent) signed CORS-enabled
 *  asset URLs for the song + its linked cover. */
export interface MvEmbedSession {
  token: string;
  embed_url: string;
  origin: string;
  expires_in: number;
  audio_url?: string;
  cover_url?: string;
}

export interface GenerateService {
  config(): Promise<GenerateConfig>;
  submit(req: GenerateRequest): Promise<GenerateResponse>;
  /** Mint an MV embed session (token + iframe URL + signed asset URLs)
   *  for designing a visualizer in MV's embedded editor. 503 when the
   *  operator hasn't configured the partner secret. */
  mvEmbedSession(musicRenderId: string): Promise<MvEmbedSession>;
  /**
   * Pre-generation review flow: run just the Claude orchestrator and
   * return the brief. The SPA then shows it in a modal where the user
   * can edit title/style/lyrics before paying the provider credits.
   * The eventual /generate call carries the edited brief via the
   * GenerateRequest.prebuiltBrief field.
   */
  brief(
    req: { prompt: string; modelId: string; reference?: string },
    // Optional progress hook — receives raw stream events as they
    // arrive ({event:"start"|"heartbeat"|"stage"|"brief"|"error", ...}).
    // The brief endpoint streams NDJSON because the orchestrator runs
    // three sequential Claude calls and frequently exceeds the
    // Cloudflare tunnel's 100s idle timeout; the heartbeats keep the
    // tunnel alive and give the SPA a "still working" signal.
    onProgress?: (event: { event: string; seconds?: number; stage?: string }) => void,
  ): Promise<{ brief: RenderBrief }>;
  batch(req: GenerateBatchRequest): Promise<GenerateBatchResponse>;
  /**
   * Upload a user-owned audio file (mp3/wav/m4a/aac/ogg/flac). The BFF
   * stores it in R2 and registers it as a music library item — from
   * there every flow (visualizer, motion, cover, share, remix) works
   * on it just like a generated track. Returns the new music job id.
   */
  uploadMusic(input: {
    file: File;
    title?: string;
  }): Promise<{ jobId: string; title: string; durationS?: number; format: string }>;
  /**
   * Upload a user-owned cover image (png/jpg/jpeg/webp/gif). Same R2 +
   * library registration path as uploadMusic, but the result is a
   * terminal image render the Image stage approves directly. Returns
   * the new image job id.
   */
  uploadImage(input: {
    file: File;
    title?: string;
  }): Promise<{ jobId: string; title: string; format: string }>;
  /**
   * Upload a user-owned video (mp4/mov/webm/m4v). Terminal video render
   * the Video stage approves directly. Returns the new video job id.
   */
  uploadVideo(input: {
    file: File;
    title?: string;
  }): Promise<{ jobId: string; title: string; durationS?: number; format: string }>;
}
export interface BundlesService {
  create(req: BundleRequest): Promise<RenderJob>;
}
export interface CreditsService {
  /**
   * Current ledger balance. Returns ``null`` (never throws) when the
   * endpoint 404s (older deployment / not yet landed) or the network
   * fails, so callers degrade to today's behaviour. ``ledgerEnabled``
   * is false when the tenant isn't on the credit ledger — callers
   * should treat that the same as ``null`` (skip the confirmation).
   */
  balance(): Promise<CreditBalance | null>;
  /*
   * NOTE: there is deliberately no `estimate()`. It used to POST a basket
   * of {sku, units} to /credits/estimate, which priced against a
   * `price_catalog` DB table — a second price list, seeded when a credit
   * was worth $0.01 and never re-based when it became worth $0.145, so it
   * quoted ~14.5x high. Nothing charged from it. Every price the UI shows
   * comes from /generate/config (`ModelOption.creditsPerGeneration`) —
   * the same numbers the backend actually charges. Sum those for a basket
   * total; do not reintroduce a pricing endpoint.
   */
}
export interface AccountService {
  me(): Promise<User>;
  plan(): Promise<Plan>;
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<void>;
}
export interface IntegrationsService {
  list(): Promise<ProviderRow[]>;
  test(providerId: string, input: { apiKey?: string }): Promise<TestResult>;
  save(providerId: string, input: SaveProviderInput): Promise<ProviderRow>;
  remove(providerId: string): Promise<void>;
  refreshModels(providerId: string): Promise<ModelOption[]>;
  refreshBalance(providerId: string): Promise<BalanceRefresh>;
}
export interface BalanceRefresh {
  supported: boolean;
  balanceLabel: string | null;
  balanceLow: boolean;
}
export interface TierMappingsService {
  list(): Promise<TierMappingsView>;
  set(kind: TierMappingCell["kind"], tier: TierMappingCell["tier"], modelId: string): Promise<TierMappingsView>;
  reset(kind: TierMappingCell["kind"], tier: TierMappingCell["tier"]): Promise<TierMappingsView>;
}
// VisualizerService removed — the client-side MediaRecorder upload
// (``upload``) and the per-aspect cover regen (``regenCover``) only
// served the in-browser recording path, which has been replaced by
// the server-side Lambda Chromium renderer. Visualizer submissions
// now go through ``services.generate.submit({ stage: "visualizer",
// composerState, ... })`` like any other render.
export interface TeamService {
  list(): Promise<TeamMember[]>;
  invite(input: { email: string; role: Role }): Promise<InviteResult>;
  update(userId: string, patch: { role?: Role; status?: TeamMember["status"] }): Promise<TeamMember>;
  remove(userId: string): Promise<void>;
  resetPassword(userId: string): Promise<{ resetUrl: string }>;
}
export interface SettingsService {
  webhooks(): Promise<WebhookRow[]>;
  addWebhook(row: { url: string; events: string }): Promise<WebhookRow>;
  saveProfile(patch: Partial<User>): Promise<User>;
}
export interface CommandService {
  list(): Promise<Command[]>;
}

export interface PaginatedPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PlatformTenantsService {
  list(q?: PlatformQuery): Promise<PaginatedPage<PlatformTenantRow>>;
  get(tenantId: string): Promise<PlatformTenantDetail>;
  patch(tenantId: string, patch: PlatformTenantPatch): Promise<PlatformTenantRow>;
  delete(tenantId: string): Promise<void>;
  grantCredits(tenantId: string, body: { amount: number; reason: string }): Promise<PlatformTenantRow>;
}
export interface PlatformUsersService {
  list(q?: PlatformQuery): Promise<PaginatedPage<PlatformUserRow>>;
  get(userId: string): Promise<PlatformUserDetail>;
  patch(userId: string, patch: PlatformUserPatch): Promise<PlatformUserRow>;
  resetPassword(userId: string): Promise<{ resetUrl: string }>;
}
export interface PlatformJobsService {
  list(q?: PlatformQuery): Promise<PaginatedPage<PlatformJobRow>>;
  get(jobId: string): Promise<PlatformJobDetail>;
}
export interface PlatformAuditService {
  list(q?: PlatformQuery): Promise<PaginatedPage<AuditEvent>>;
}
export interface PlatformLookupsService {
  /** Spec: `string[]` (plain plan names). */
  plans(): Promise<string[]>;
  /** Spec: `{ tenantId, name }[]`. */
  tenants(): Promise<PlatformTenantLookup[]>;
}
export interface PlatformSettingsService {
  get(): Promise<PlatformSettings>;
  putOauth(provider: string, body: { clientId: string; clientSecret: string }): Promise<PlatformOAuthProviderState>;
  deleteOauth(provider: string): Promise<void>;
  putSmtp(body: PlatformSmtpWrite): Promise<PlatformSettings["smtp"]>;
  deleteSmtp(): Promise<void>;
  putStripe(body: PlatformStripeWrite): Promise<PlatformSettings["stripe"]>;
  putCloudflare(body: PlatformCloudflareWrite): Promise<PlatformSettings["cloudflare"]>;
  putGithub(body: PlatformGithubWrite): Promise<PlatformSettings["github"]>;
  putOrigins(body: PlatformOriginsWrite): Promise<PlatformSettings["origins"]>;
}
export interface PlatformDiagnosticsResponse {
  apiEnv: {
    name: string;
    label: string;
    set: boolean;
    purpose: string;
  }[];
  orchestratorEnabled: boolean;
  note: string;
}

export interface PlatformReloadResponse {
  ok: boolean;
  message: string;
  durationSeconds?: number;
}

export interface PlatformService {
  stats(): Promise<PlatformStats>;
  diagnostics(): Promise<PlatformDiagnosticsResponse>;
  reload(): Promise<PlatformReloadResponse>;
  tenants: PlatformTenantsService;
  users: PlatformUsersService;
  jobs: PlatformJobsService;
  audit: PlatformAuditService;
  lookups: PlatformLookupsService;
  settings: PlatformSettingsService;
}

export interface Services {
  dashboard: DashboardService;
  library: LibraryService;
  renders: RendersService;
  channels: ChannelsService;
  generate: GenerateService;
  bundles: BundlesService;
  credits: CreditsService;
  account: AccountService;
  settings: SettingsService;
  commands: CommandService;
  integrations: IntegrationsService;
  tierMappings: TierMappingsService;
  team: TeamService;
  platform: PlatformService;
}

/* ---------- Mock implementations ---------- */
const mockServices: Services = {
  dashboard: {
    getDashboard: () =>
      mockResolve(
        {
          user: fx.CURRENT_USER,
          stats: fx.DASHBOARD_STATS,
          active: fx.ACTIVE_GENERATIONS,
          creditBreakdown: fx.CREDIT_BREAKDOWN,
          channels: fx.CHANNEL_SUMMARIES,
          activity: fx.ACTIVITY,
          recent: fx.RECENT_WORK,
        } satisfies DashboardData,
        "dashboard.get",
      ),
  },
  library: {
    list: () => mockResolve(fx.LIBRARY_ITEMS, "library.list"),
    get: (id) =>
      mockResolve(
        [...fx.LIBRARY_ITEMS, ...fx.LIBRARY_COMPONENTS].find((i) => i.id === id),
        "library.get",
      ),
    publish: (_id, body) =>
      mockResolve(
        {
          status: body.scheduleAt ? "scheduled" : "partial",
          label: body.scheduleAt ? `Scheduled for ${body.scheduleAt}` : "Publishing",
          destinations: body.channels.map((p) => ({
            icon: p[0]?.toUpperCase() ?? "?",
            platform: p,
            handle: "—",
            time: "—",
          })),
        } satisfies PublishState,
        "library.publish",
      ),
    delete: (_id) => mockResolve(undefined, "library.delete"),
    bulkDelete: (ids) =>
      mockResolve({ deleted: ids, failed: [] }, "library.bulkDelete"),
    setCoverLink: (musicId, imageRenderId) =>
      mockResolve(
        { coverImageUrl: `/mock/${musicId}/cover`, imageRenderId },
        "library.setCoverLink",
      ),
    clearCoverLink: (_id) => mockResolve(undefined, "library.clearCoverLink"),
  },
  renders: {
    list: (tab) =>
      mockResolve(
        fx.RENDER_JOBS.filter((r) => r.tab === tab),
        "renders.list",
      ),
    counts: () => mockResolve({ ...fx.RENDER_TAB_COUNTS }, "renders.counts"),
    detail: async (id) => {
      const j = fx.RENDER_JOBS.find((r) => r.id === id);
      if (!j) throw new Error("not found");
      return mockResolve(
        {
          id: j.id,
          title: j.title,
          kind: (j.type as RenderDetail["kind"]) ?? "music",
          state: j.state,
          progress: j.progress,
          elapsed: j.elapsed,
          cost: j.cost,
          model: j.model,
          provider: undefined,
          prompt: undefined,
          created: new Date().toISOString(),
          components: [],
          versions: [],
        } satisfies RenderDetail,
        "renders.detail",
      );
    },
    download: (id) =>
      mockResolve(
        { jobId: id, state: "complete", message: "download ready", url: "#" } satisfies RenderActionResponse,
        "renders.download",
      ),
    regenerate: (id, _overrides) =>
      mockResolve(
        { jobId: `${id}_regen`, state: "queued", message: "render queued" } satisfies RenderActionResponse,
        "renders.regenerate",
      ),
    artPrompt: (_id, _setup) =>
      mockResolve(
        {
          artworkPrompt:
            "Album cover, melancholic Persian classical, ney instrument silhouette against indigo night sky, single warm candle, calligraphic flourishes in gold foil",
          composition: "Centered subject with negative space, symmetric framing",
          palette: ["#0e0a1e", "#3b2a6b", "#d6a35c"],
          lighting: "Single warm key light, deep shadows, intimate",
          aspectRatioHint: "1:1",
          references: ["Hossein Behzad miniature illustration", "Iranian poetry book covers"],
        },
        "renders.artPrompt",
      ),
    translateLyrics: (_id, targetLanguage) =>
      mockResolve(
        {
          body: `[Verse 1]\n(Translation preview — ${targetLanguage})\n`,
          notes: "Mock translation",
          targetLanguage,
          sourceLanguage: "English",
        },
        "renders.translateLyrics",
      ),
    motionPrompt: (_id, _options) =>
      mockResolve(
        {
          motionPrompt: "Slow push-in on the central subject; soft parallax background.",
          cameraMoves: ["push-in", "slow pan", "rack focus"],
          pacing: "Gentle, contemplative",
          transitions: "Cross-dissolves on chorus",
          focalSubject: "Album cover composition",
        },
        "renders.motionPrompt",
      ),
    analyzeTrack: (_id) =>
      mockResolve<TrackAnalysis | null>(
        {
          bpm: 120,
          key: "A minor",
          durationS: 184,
          genre: "afro house",
          mood: "warm, hypnotic",
          cached: false,
        },
        "renders.analyzeTrack",
      ),
    publish: (_id, body) =>
      mockResolve(
        {
          status: body.scheduleAt ? "scheduled" : "partial",
          label: body.scheduleAt ? `Scheduled for ${body.scheduleAt}` : "Publishing",
          destinations: body.channels.map((p) => ({
            icon: p[0]?.toUpperCase() ?? "?",
            platform: p,
            handle: "—",
            time: "—",
          })),
        } satisfies PublishState,
        "renders.publish",
      ),
    streamUrl: () => "",
    signUrl: (id) =>
      mockResolve(
        { url: `/mock/renders/${id}/public-file?token=mock`, expiresIn: 3600 },
        "renders.signUrl",
      ),
  },
  channels: {
    list: () => mockResolve(fx.CHANNELS, "channels.list"),
    connect: async (platform) =>
      mockResolve(
        {
          platform,
          authorizeUrl: `/mock-oauth/${platform}`,
          state: "mock-state",
        } satisfies ChannelConnectResponse,
        "channels.connect",
      ),
    connectStart: async () =>
      mockResolve({ authorizeUrl: "/mock-oauth/youtube", state: "mock-state" }, "channels.connectStart"),
    disconnect: () => mockResolve(undefined, "channels.disconnect"),
  },
  generate: {
    config: () => mockResolve(fx.GENERATE_CONFIG, "generate.config"),
    mvEmbedSession: (musicRenderId) =>
      mockResolve(
        {
          token: `mock-embed-token-${musicRenderId}`,
          embed_url: "https://musicvisualizer.pro/embed/v1/editor",
          origin: "https://app.vocaler.ai",
          expires_in: 900,
          audio_url: "https://example.com/mock-audio.mp3",
        },
        "generate.mvEmbedSession"
      ),
    submit: async (req) => {
      const all = [
        ...fx.GENERATE_CONFIG.musicModels,
        ...fx.GENERATE_CONFIG.imageModels,
        ...fx.GENERATE_CONFIG.videoModels,
      ];
      const model = all.find((m) => m.id === req.modelId);
      const creditsCharged = (model?.creditsPerGeneration ?? 0) * req.generations;
      return mockResolve(
        {
          jobId: `gen_${Math.random().toString(16).slice(2, 8)}`,
          stage: req.stage,
          creditsCharged,
          state: "rendering" as const,
        } satisfies GenerateResponse,
        "generate.submit",
      );
    },
    brief: async (req) => {
      // Mock: return a deterministic-ish brief so the dev SPA can
      // exercise the review modal without an API container.
      return mockResolve(
        {
          brief: {
            title: "Crimson Wind",
            genre: "persian",
            subgenre: "classical",
            mood: "melancholic, contemplative",
            style: "persian classical, ney, santur, melancholic, slow",
            bpm: 72,
            key: "Dastgah Shur",
            songStructure: "Daramad-Avaz-Tasnif-Reng",
            instrumentation: ["ney", "santur", "tombak"],
            hasVocals: true,
            lyricsBody: "[Verse 1]\nسرخ بادی می‌وزد\n[Chorus]\nقلبم می‌داند",
            hookLine: "قلبم می‌داند",
            language: "fa",
          } satisfies RenderBrief,
        },
        `generate.brief(${req.modelId})`,
      );
    },
    batch: async (req) => {
      const results = req.rows.map((r, i) => ({
        index: i,
        name: r.name,
        valid: Boolean(r.name && r.prompt.trim()),
        issue: r.name && r.prompt.trim() ? undefined : "prompt is required",
      }));
      const queued = results.filter((r) => r.valid).length;
      return mockResolve(
        {
          queued,
          creditsCharged: queued * 8,
          jobIds: results.filter((r) => r.valid).map((_r, i) => `gen_${i}`),
          rows: results,
        } satisfies GenerateBatchResponse,
        "generate.batch",
      );
    },
    uploadMusic: async ({ file, title }) =>
      mockResolve(
        {
          jobId: `up_${Math.random().toString(16).slice(2, 8)}`,
          title: title || file.name.replace(/\.[^.]+$/, ""),
          durationS: 180,
          format: file.name.split(".").pop() ?? "mp3",
        },
        "generate.uploadMusic",
      ),
    uploadImage: async ({ file, title }) =>
      mockResolve(
        {
          jobId: `mock_upload_${Date.now()}`,
          title: title || file.name.replace(/\.[^.]+$/, ""),
          format: file.name.split(".").pop() ?? "png",
        },
        "generate.uploadImage",
      ),
    uploadVideo: async ({ file, title }) =>
      mockResolve(
        {
          jobId: `mock_upload_${Date.now()}`,
          title: title || file.name.replace(/\.[^.]+$/, ""),
          durationS: 12,
          format: file.name.split(".").pop() ?? "mp4",
        },
        "generate.uploadVideo",
      ),
  },
  bundles: {
    create: async (req) => {
      const cost = 8 + 1 * (req.videoIds.length - 1);
      return mockResolve(
        {
          id: `bnd_${Math.random().toString(16).slice(2, 8)}`,
          state: "queued",
          title: req.name,
          type: "bundle",
          model: "ffmpeg",
          progress: 0,
          elapsed: "00:00:00",
          cost,
          tab: "active",
        } satisfies RenderJob,
        "bundles.create",
      );
    },
  },
  credits: {
    // Mock ledger: a fixed balance so the dev SPA can exercise the
    // balance chip. Real numbers come from the BFF. Prices are NOT mocked
    // here — they come from /generate/config like everywhere else.
    balance: async () =>
      mockResolve<CreditBalance | null>(
        { balance: "240", ledgerEnabled: true },
        "credits.balance",
      ),
  },
  account: {
    me: () => mockResolve(fx.CURRENT_USER, "account.me"),
    plan: () => mockResolve(fx.CURRENT_PLAN, "account.plan"),
    changePassword: async ({ currentPassword, newPassword }) => {
      await mockResolve(null, "account.changePassword");
      if (currentPassword !== "correct-horse")
        throw new ApiError("Current password is incorrect.", 422, "BAD_PASSWORD");
      if (newPassword.length < 12)
        throw new ApiError("Password must be at least 12 characters.", 422, "WEAK_PASSWORD");
    },
  },
  integrations: {
    list: () => mockResolve(mockProviders, "integrations.list"),
    test: async (providerId, { apiKey }) => {
      await mockResolve(null, "integrations.test");
      const p = mockProviders.find((x) => x.id === providerId);
      const bad = apiKey ? /bad|fail|wrong/i.test(apiKey) : p?.lastTestOk === false;
      return bad
        ? { ok: false, latencyMs: 420, message: `${p?.label ?? providerId} rejected the key (401 unauthorized).` }
        : { ok: true, latencyMs: 180, message: "Authenticated.", probed: { models: p?.defaultModels.map((m) => m.id), balanceUsd: p?.creditsAvailableUsd ?? undefined } };
    },
    save: async (providerId, input) => {
      await mockResolve(null, "integrations.save");
      const i = mockProviders.findIndex((x) => x.id === providerId);
      if (i < 0) throw new ApiError("Unknown provider.", 404);
      const next: ProviderRow = {
        ...mockProviders[i]!,
        configured: true,
        enabled: input.enabled ?? true,
        lastTestAt: new Date().toISOString(),
        lastTestOk: true,
      };
      mockProviders[i] = next;
      return structuredClone(next);
    },
    remove: async (providerId) => {
      await mockResolve(null, "integrations.remove");
      const i = mockProviders.findIndex((x) => x.id === providerId);
      if (i >= 0)
        mockProviders[i] = { ...mockProviders[i]!, configured: false, enabled: false, lastTestOk: null, lastTestAt: null };
    },
    refreshModels: async (providerId) => {
      await mockResolve(null, "integrations.refreshModels");
      return structuredClone(mockProviders.find((x) => x.id === providerId)?.defaultModels ?? []);
    },
    refreshBalance: async (providerId) => {
      await mockResolve(null, "integrations.refreshBalance");
      const p = mockProviders.find((x) => x.id === providerId);
      return { supported: p?.balanceLabel != null, balanceLabel: p?.balanceLabel ?? null, balanceLow: p?.balanceLow ?? false };
    },
  },
  tierMappings: {
    list: () => mockResolve(mockTierMappings(), "tierMappings.list"),
    set: async (kind, tier, modelId) => {
      await mockResolve(null, "tierMappings.set");
      mockTierState[kind][tier] = modelId;
      return mockTierMappings();
    },
    reset: async (kind, tier) => {
      await mockResolve(null, "tierMappings.reset");
      delete mockTierState[kind][tier];
      return mockTierMappings();
    },
  },
  team: {
    list: () => mockResolve(mockTeam, "team.list"),
    invite: async ({ email, role }) => {
      await mockResolve(null, "team.invite");
      const inviteId = `inv_${Math.random().toString(16).slice(2, 8)}`;
      mockTeam.push({ userId: `usr_${inviteId}`, email, name: null, role, status: "invited", emailVerifiedAt: null, lastSeenAt: null, isSelf: false });
      return { inviteId, inviteUrl: `https://app.vocaler.ai/accept-invite/${inviteId}` };
    },
    update: async (userId, patch) => {
      await mockResolve(null, "team.update");
      const i = mockTeam.findIndex((m) => m.userId === userId);
      if (i < 0) throw new ApiError("Member not found.", 404);
      if (patch.role && mockTeam[i]!.role === "owner" && patch.role !== "owner") {
        const owners = mockTeam.filter((m) => m.role === "owner").length;
        if (owners <= 1) throw new ApiError("Cannot demote the last owner.", 409, "LAST_OWNER");
      }
      const next = { ...mockTeam[i]!, ...patch };
      mockTeam[i] = next;
      return structuredClone(next);
    },
    remove: async (userId) => {
      await mockResolve(null, "team.remove");
      const m = mockTeam.find((x) => x.userId === userId);
      if (m?.role === "owner" && mockTeam.filter((x) => x.role === "owner").length <= 1)
        throw new ApiError("Cannot remove the last owner.", 409, "LAST_OWNER");
      mockTeam = mockTeam.filter((x) => x.userId !== userId);
    },
    resetPassword: async (userId) => {
      await mockResolve(null, "team.resetPassword");
      return { resetUrl: `https://app.vocaler.ai/reset/${userId}-${Math.random().toString(16).slice(2, 8)}` };
    },
  },
  settings: {
    webhooks: () => mockResolve(fx.WEBHOOKS, "settings.webhooks"),
    addWebhook: (row) =>
      mockResolve(
        { ...row, status: "active", lastTriggered: "—" } satisfies WebhookRow,
        "settings.addWebhook",
      ),
    saveProfile: (patch) => mockResolve({ ...fx.CURRENT_USER, ...patch }, "settings.saveProfile"),
  },
  commands: {
    list: () => mockResolve(fx.COMMANDS, "commands.list"),
  },
  platform: {
    stats: () =>
      mockResolve(
        {
          tenants: { total: 14, active: 13, suspended: 1, newThisMonth: 3 },
          users: { total: 22, operators: 1, active: 20, suspended: 0 },
          jobs: { total: 1437, active: 5, completed: 1402, failed: 30, last24h: 47 },
          revenue: { creditsChargedThisMonth: 12480, estimatedUsdThisMonth: null },
          topProviders: [
            { id: "suno-v5", label: "Suno V5", jobsLast30d: 612, creditsLast30d: 4896 },
            { id: "flux-schnell", label: "Flux Schnell", jobsLast30d: 211, creditsLast30d: 211 },
            { id: "kling", label: "Kling 1.6", jobsLast30d: 88, creditsLast30d: 1320 },
          ],
        } satisfies PlatformStats,
        "platform.stats",
      ),
    diagnostics: () =>
      mockResolve(
        {
          apiEnv: [
            { name: "ANTHROPIC_API_KEY", label: "Claude (orchestrator + brief)", set: true,
              purpose: "Powers the Concept Director / Music Producer / Lyricist personas plus the best-of music reviewer." },
            { name: "REELFORGE_SUNO_API_KEY", label: "Suno", set: true,
              purpose: "Music generation — Suno v4/v5, Best-of vocal stem, Hybrid Cinematic vocal stem." },
            { name: "REELFORGE_LYRIA_API_KEY", label: "Lyria", set: false,
              purpose: "Music generation — Lyria 3 Pro/Clip, Best-of bed stem, Hybrid Cinematic bed stem." },
            { name: "REELFORGE_REPLICATE_API_TOKEN", label: "Replicate", set: true,
              purpose: "Image (Flux Schnell/Dev, SDXL) + video (Kling) + MusicGen." },
            { name: "REELFORGE_REVE_API_KEY", label: "Reve", set: false,
              purpose: "Image generation — Reve and Reve Alpha models." },
          ],
          orchestratorEnabled: true,
          note: "Mock data — real diagnostics shown when running against the live API.",
        } satisfies PlatformDiagnosticsResponse,
        "platform.diagnostics",
      ),
    reload: () =>
      mockResolve(
        { ok: true, message: "Mock reload — no containers actually restarted.", durationSeconds: 0.1 } satisfies PlatformReloadResponse,
        "platform.reload",
      ),
    tenants: {
      list: () =>
        mockResolve(
          {
            items: fx.PLATFORM_TENANTS,
            total: fx.PLATFORM_TENANTS.length,
            page: 1,
            pageSize: 25,
          },
          "platform.tenants.list",
        ),
      get: (tenantId) =>
        mockResolve(
          (fx.PLATFORM_TENANT_DETAILS as Record<string, PlatformTenantDetail>)[tenantId] ??
            { ...fx.PLATFORM_TENANTS[0]!, members: [], recentJobs: [], recentEvents: [], integrations: [], billing: { stripeCustomerId: null, subscriptionStatus: null, currentPeriodEnd: null, paymentFailureCount: 0 } },
          "platform.tenants.get",
        ),
      patch: (tenantId, patch) =>
        mockResolve(
          { ...fx.PLATFORM_TENANTS.find((t) => t.tenantId === tenantId)!, ...patch } as PlatformTenantRow,
          "platform.tenants.patch",
        ),
      delete: () => mockResolve(undefined, "platform.tenants.delete"),
      grantCredits: (tenantId, body) =>
        mockResolve(
          {
            ...fx.PLATFORM_TENANTS.find((t) => t.tenantId === tenantId)!,
            bonusCredits:
              (fx.PLATFORM_TENANTS.find((t) => t.tenantId === tenantId)?.bonusCredits ?? 0) + body.amount,
          } as PlatformTenantRow,
          "platform.tenants.grantCredits",
        ),
    },
    users: {
      list: () =>
        mockResolve(
          { items: fx.PLATFORM_USERS, total: fx.PLATFORM_USERS.length, page: 1, pageSize: 25 },
          "platform.users.list",
        ),
      get: (userId) =>
        mockResolve(
          (fx.PLATFORM_USER_DETAILS as Record<string, PlatformUserDetail>)[userId] ??
            { ...fx.PLATFORM_USERS[0]!, memberships: [], recentActivity: [] },
          "platform.users.get",
        ),
      patch: (userId, patch) =>
        mockResolve(
          { ...fx.PLATFORM_USERS.find((u) => u.userId === userId)!, ...patch } as PlatformUserRow,
          "platform.users.patch",
        ),
      resetPassword: () =>
        mockResolve({ resetUrl: "https://api.vocaler.ai/api/v1/auth/verify?token=mock&reset=1" }, "platform.users.resetPassword"),
    },
    jobs: {
      list: () =>
        mockResolve(
          { items: fx.PLATFORM_JOBS, total: fx.PLATFORM_JOBS.length, page: 1, pageSize: 50 },
          "platform.jobs.list",
        ),
      get: (jobId) =>
        mockResolve(
          { ...fx.PLATFORM_JOBS.find((j) => j.jobId === jobId)!, params: {}, events: [], artifacts: [], costEvents: [] } as PlatformJobDetail,
          "platform.jobs.get",
        ),
    },
    audit: {
      list: () =>
        mockResolve(
          { items: fx.PLATFORM_AUDIT, total: fx.PLATFORM_AUDIT.length, page: 1, pageSize: 50 },
          "platform.audit.list",
        ),
    },
    lookups: {
      plans: () =>
        mockResolve(["free", "pro", "studio", "enterprise"], "platform.lookups.plans"),
      tenants: () =>
        mockResolve(
          fx.PLATFORM_TENANTS.map((t) => ({ tenantId: t.tenantId, name: t.name })),
          "platform.lookups.tenants",
        ),
    },
    settings: {
      get: () => mockResolve(MOCK_SETTINGS, "platform.settings.get"),
      putOauth: (provider, body) =>
        mockResolve(
          { provider, label: provider, clientId: body.clientId, clientSecretMasked: "••••" + body.clientSecret.slice(-4), configured: true },
          "platform.settings.oauth.put",
        ),
      deleteOauth: () => mockResolve(undefined, "platform.settings.oauth.delete"),
      putSmtp: () => mockResolve(MOCK_SETTINGS.smtp, "platform.settings.smtp.put"),
      deleteSmtp: () => mockResolve(undefined, "platform.settings.smtp.delete"),
      putStripe: () => mockResolve(MOCK_SETTINGS.stripe, "platform.settings.stripe.put"),
      putCloudflare: () => mockResolve(MOCK_SETTINGS.cloudflare, "platform.settings.cloudflare.put"),
      putGithub: () => mockResolve(MOCK_SETTINGS.github, "platform.settings.github.put"),
      putOrigins: () => mockResolve(MOCK_SETTINGS.origins, "platform.settings.origins.put"),
    },
  },
};

const MOCK_SETTINGS: PlatformSettings = {
  oauth: {
    providers: [
      { provider: "google", label: "Google", clientId: null, clientSecretMasked: null, configured: false },
      { provider: "microsoft", label: "Microsoft", clientId: null, clientSecretMasked: null, configured: false },
      { provider: "facebook", label: "Facebook", clientId: null, clientSecretMasked: null, configured: false },
      { provider: "x", label: "X / Twitter", clientId: null, clientSecretMasked: null, configured: false },
    ],
  },
  smtp: { host: null, port: null, username: null, passwordMasked: null, fromAddr: null, useTls: null, useSsl: null, configured: false },
  stripe: { publishableKey: null, secretKeyMasked: null, webhookSecretMasked: null, configured: false },
  cloudflare: { apiTokenMasked: null, accountId: null, tunnelId: null, configured: false },
  github: { webhookSecretMasked: null, repo: null, configured: false },
  origins: { appBaseUrl: null, callbackBase: null },
};

/* ---------- HTTP implementations (talk to /api/v1/web/*) ---------- */
const httpServices: Services = {
  dashboard: { getDashboard: () => http<DashboardData>("/dashboard") },
  library: {
    list: (opts) => {
      const qs = new URLSearchParams();
      if (opts?.type) qs.set("type", opts.type);
      if (opts?.q) qs.set("q", opts.q);
      const tail = qs.toString();
      return http<LibraryItem[]>(`/library${tail ? `?${tail}` : ""}`);
    },
    get: (id) => http<LibraryItem>(`/library/${id}`),
    publish: (id, body) =>
      http<PublishState>(`/library/${id}/publish`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    delete: (id) => http<void>(`/library/${id}`, { method: "DELETE" }),
    bulkDelete: (ids) =>
      http<{ deleted: string[]; failed: string[] }>(`/library/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    setCoverLink: (musicId, imageRenderId) =>
      http<{ coverImageUrl: string; imageRenderId: string }>(
        `/library/${musicId}/cover-link`,
        { method: "PUT", body: JSON.stringify({ imageRenderId }) },
      ),
    clearCoverLink: (id) =>
      http<void>(`/library/${id}/cover-link`, { method: "DELETE" }),
  },
  renders: {
    list: (tab) => http<RenderJob[]>(`/renders?tab=${tab}`),
    counts: () => http<Record<RenderTab, number>>("/renders/counts"),
    detail: (id) => http<RenderDetail>(`/renders/${id}`),
    download: (id) =>
      http<RenderActionResponse>(`/renders/${id}/download`, { method: "POST" }),
    regenerate: (id, overrides) =>
      http<RenderActionResponse>(`/renders/${id}/regenerate`, {
        method: "POST",
        body: overrides ? JSON.stringify(overrides) : undefined,
      }),
    artPrompt: (id, setup) => {
      // Only send keys that are present + non-empty so a brief-carrying
      // render still gets the empty-body behaviour, and a brief-less one
      // gets whatever the user filled in the setup panel.
      const body: Record<string, string> = {};
      if (setup?.genre?.trim()) body.genre = setup.genre.trim();
      if (setup?.mood?.trim()) body.mood = setup.mood.trim();
      if (setup?.title?.trim()) body.title = setup.title.trim();
      const hasBody = Object.keys(body).length > 0;
      return http<{
        artworkPrompt: string;
        composition?: string;
        palette: string[];
        lighting?: string;
        aspectRatioHint?: string;
        references: string[];
      }>(`/renders/${id}/art-prompt`, {
        method: "POST",
        ...(hasBody ? { body: JSON.stringify(body) } : {}),
      });
    },
    translateLyrics: (id, targetLanguage) =>
      http<{
        body: string;
        notes?: string;
        targetLanguage: string;
        sourceLanguage?: string;
      }>(`/renders/${id}/translate-lyrics`, {
        method: "POST",
        body: JSON.stringify({ targetLanguage }),
      }),
    motionPrompt: (id, options) => {
      const qs = options?.targetEngine
        ? `?targetEngine=${encodeURIComponent(options.targetEngine)}`
        : "";
      return http<{
        motionPrompt: string;
        cameraMoves: string[];
        pacing?: string;
        transitions?: string;
        focalSubject?: string;
      }>(`/renders/${id}/motion-prompt${qs}`, { method: "POST" });
    },
    publish: (id, body) =>
      http<PublishState>(`/renders/${id}/publish`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Swallows failures and resolves to null (never throws) so the
    // remix-setup panel degrades to "couldn't analyze the audio" and
    // the flow is never blocked. The first call runs librosa (~1-3s);
    // the BFF caches afterwards.
    analyzeTrack: async (jobId) => {
      try {
        return await http<TrackAnalysis>(`/tracks/${jobId}/analyze`, {
          method: "POST",
        });
      } catch {
        return null;
      }
    },
    streamUrl: () => `${env.apiBaseUrl}/renders/stream`,
    signUrl: (id) =>
      http<{ url: string; expiresIn: number }>(`/renders/${id}/sign-url`, {
        method: "POST",
      }),
  },
  channels: {
    list: () => http<Channel[]>("/channels"),
    connect: (platform, handle) =>
      http<ChannelConnectResponse>("/channels/connect", {
        method: "POST",
        body: JSON.stringify({ platform, handle }),
      }),
    connectStart: (platform) =>
      http<{ authorizeUrl: string; state: string }>(`/channels/connect/${platform}/start`, {
        method: "POST",
      }),
    disconnect: (id) => http<void>(`/channels/${id}`, { method: "DELETE" }),
  },
  generate: {
    config: () => http<GenerateConfig>("/generate/config"),
    submit: (req) =>
      http<GenerateResponse>("/generate", { method: "POST", body: JSON.stringify(req) }),
    mvEmbedSession: (musicRenderId) =>
      http<MvEmbedSession>("/embed/mv-token", {
        method: "POST",
        body: JSON.stringify({ musicRenderId }),
      }),
    brief: async (req, onProgress) => {
      // NDJSON-streamed response — read line-by-line, surface progress
      // to the caller via onProgress, and return the final {brief}
      // payload. Throws ApiError on either an HTTP-level failure or a
      // terminal {event:"error"} line in the stream.
      const res = await fetch(`${env.apiBaseUrl}/generate/brief`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      });
      if (!res.ok) {
        let message = res.statusText || `HTTP ${res.status}`;
        try {
          const j = (await res.clone().json()) as { detail?: string; message?: string };
          message = j.detail ?? j.message ?? message;
        } catch {
          /* non-JSON */
        }
        throw new ApiError(message, res.status);
      }
      if (!res.body) {
        throw new ApiError("brief stream had no body", 502);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let final: { brief: RenderBrief } | null = null;
      let streamErr: { status: number; message: string } | null = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (value) buf += decoder.decode(value, { stream: true });
        let nl = buf.indexOf("\n");
        while (nl >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          nl = buf.indexOf("\n");
          if (!line) continue;
          try {
            const ev = JSON.parse(line) as {
              event: string;
              brief?: RenderBrief;
              message?: string;
              status?: number;
            };
            onProgress?.(ev);
            if (ev.event === "brief" && ev.brief) {
              final = { brief: ev.brief };
            } else if (ev.event === "error") {
              streamErr = {
                status: ev.status ?? 502,
                message: ev.message ?? "orchestrator failed",
              };
            }
          } catch {
            /* skip unparseable lines */
          }
        }
        if (done) break;
      }
      if (streamErr) throw new ApiError(streamErr.message, streamErr.status);
      if (!final) throw new ApiError("brief stream ended without a brief", 502);
      return final;
    },
    batch: (req) =>
      http<GenerateBatchResponse>("/generate/batch", {
        method: "POST",
        body: JSON.stringify(req),
      }),
    uploadMusic: async ({ file, title }) => {
      // Multipart upload — hit fetch directly so the browser sets the
      // multipart boundary header itself (http() forces JSON). Cookie
      // auth via credentials:"include".
      const fd = new FormData();
      fd.append("file", file, file.name);
      if (title) fd.append("title", title);
      const res = await fetch(`${env.apiBaseUrl}/uploads/music`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        let message = res.statusText || `HTTP ${res.status}`;
        try {
          const j = (await res.clone().json()) as { detail?: string; message?: string };
          message = j.detail ?? j.message ?? message;
        } catch {
          /* non-JSON */
        }
        throw new ApiError(message, res.status);
      }
      return res.json() as Promise<{
        jobId: string;
        title: string;
        durationS?: number;
        format: string;
      }>;
    },
    uploadImage: async ({ file, title }) => {
      // Multipart upload — direct fetch so the browser sets the
      // multipart boundary header (http() forces JSON). Cookie auth
      // via credentials:"include". Mirrors uploadMusic exactly.
      const fd = new FormData();
      fd.append("file", file, file.name);
      if (title) fd.append("title", title);
      const res = await fetch(`${env.apiBaseUrl}/uploads/image`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        let message = res.statusText || `HTTP ${res.status}`;
        try {
          const j = (await res.clone().json()) as { detail?: string; message?: string };
          message = j.detail ?? j.message ?? message;
        } catch {
          /* non-JSON */
        }
        throw new ApiError(message, res.status);
      }
      return res.json() as Promise<{
        jobId: string;
        title: string;
        format: string;
      }>;
    },
    uploadVideo: async ({ file, title }) => {
      const fd = new FormData();
      fd.append("file", file, file.name);
      if (title) fd.append("title", title);
      const res = await fetch(`${env.apiBaseUrl}/uploads/video`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        let message = res.statusText || `HTTP ${res.status}`;
        try {
          const j = (await res.clone().json()) as { detail?: string; message?: string };
          message = j.detail ?? j.message ?? message;
        } catch {
          /* non-JSON */
        }
        throw new ApiError(message, res.status);
      }
      return res.json() as Promise<{
        jobId: string;
        title: string;
        durationS?: number;
        format: string;
      }>;
    },
  },
  bundles: {
    create: (req) =>
      http<RenderJob>("/bundles", { method: "POST", body: JSON.stringify(req) }),
  },
  credits: {
    // Swallows failures and resolves to null so the credit chip degrades
    // to today's behaviour whenever the ledger endpoint isn't there (404
    // in older deployments) or the network blips.
    balance: async () => {
      try {
        return await http<CreditBalance>("/credits/balance");
      } catch {
        return null;
      }
    },
  },
  account: {
    me: () => http<User>("/account/me"),
    plan: () => http<Plan>("/account/plan"),
    // env.apiBaseUrl already ends in /api/v1/web; paths here MUST NOT
    // re-prefix /web/ or they'd double-up. Same pattern as /dashboard,
    // /library, /renders, etc. above.
    changePassword: (input) =>
      http<void>("/account/password", { method: "POST", body: JSON.stringify(input) }),
  },
  integrations: {
    list: () => http<ProviderRow[]>("/integrations"),
    test: (providerId, input) =>
      http<TestResult>(`/integrations/${providerId}/test`, { method: "POST", body: JSON.stringify(input) }),
    save: (providerId, input) =>
      http<ProviderRow>(`/integrations/${providerId}`, { method: "PUT", body: JSON.stringify(input) }),
    remove: (providerId) => http<void>(`/integrations/${providerId}`, { method: "DELETE" }),
    refreshModels: (providerId) =>
      http<ModelOption[]>(`/integrations/${providerId}/models`, { method: "POST" }),
    refreshBalance: (providerId) =>
      http<BalanceRefresh>(`/integrations/${providerId}/balance/refresh`, { method: "POST" }),
  },
  tierMappings: {
    list: () => http<TierMappingsView>("/admin/tier-mappings"),
    set: (kind, tier, modelId) =>
      http<TierMappingsView>(`/admin/tier-mappings/${kind}/${tier}`, {
        method: "PUT",
        body: JSON.stringify({ modelId }),
      }),
    reset: (kind, tier) =>
      http<TierMappingsView>(`/admin/tier-mappings/${kind}/${tier}`, { method: "DELETE" }),
  },
  team: {
    list: () => http<TeamMember[]>("/team"),
    invite: (input) => http<InviteResult>("/team/invite", { method: "POST", body: JSON.stringify(input) }),
    update: (userId, patch) =>
      http<TeamMember>(`/team/${userId}`, { method: "PATCH", body: JSON.stringify(patch) }),
    remove: (userId) => http<void>(`/team/${userId}`, { method: "DELETE" }),
    resetPassword: (userId) =>
      http<{ resetUrl: string }>(`/team/${userId}/reset-password`, { method: "POST" }),
  },
  settings: {
    webhooks: () => http<WebhookRow[]>("/settings/webhooks"),
    addWebhook: (row) =>
      http<WebhookRow>("/settings/webhooks", {
        method: "POST",
        body: JSON.stringify(row),
      }),
    saveProfile: (patch) =>
      http<User>("/account/profile", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },
  commands: { list: () => http<Command[]>("/commands") },
  platform: {
    stats: () => http<PlatformStats>("/platform/stats"),
    diagnostics: () => http<PlatformDiagnosticsResponse>("/platform/diagnostics"),
    reload: () =>
      http<PlatformReloadResponse>("/platform/reload", { method: "POST" }),
    tenants: {
      list: (q) =>
        http<PaginatedPage<PlatformTenantRow>>(`/platform/tenants${platformQs(q)}`),
      get: (tenantId) => http<PlatformTenantDetail>(`/platform/tenants/${tenantId}`),
      patch: (tenantId, patch) =>
        http<PlatformTenantRow>(`/platform/tenants/${tenantId}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      delete: (tenantId) =>
        http<void>(`/platform/tenants/${tenantId}`, { method: "DELETE" }),
      grantCredits: (tenantId, body) =>
        http<PlatformTenantRow>(`/platform/tenants/${tenantId}/credits/grant`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
    },
    users: {
      list: (q) =>
        http<PaginatedPage<PlatformUserRow>>(`/platform/users${platformQs(q)}`),
      get: (userId) => http<PlatformUserDetail>(`/platform/users/${userId}`),
      patch: (userId, patch) =>
        http<PlatformUserRow>(`/platform/users/${userId}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      resetPassword: (userId) =>
        http<{ resetUrl: string }>(`/platform/users/${userId}/reset-password`, { method: "POST" }),
    },
    jobs: {
      list: (q) =>
        http<PaginatedPage<PlatformJobRow>>(`/platform/jobs${platformQs(q)}`),
      get: (jobId) => http<PlatformJobDetail>(`/platform/jobs/${jobId}`),
    },
    audit: {
      list: (q) =>
        http<PaginatedPage<AuditEvent>>(`/platform/audit${platformQs(q)}`),
    },
    lookups: {
      plans: () => http<string[]>("/platform/lookups/plans"),
      tenants: () => http<PlatformTenantLookup[]>("/platform/lookups/tenants"),
    },
    settings: {
      get: () => http<PlatformSettings>("/platform/settings"),
      putOauth: (provider, body) =>
        http<PlatformOAuthProviderState>(`/platform/settings/oauth/${provider}`, {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      deleteOauth: (provider) =>
        http<void>(`/platform/settings/oauth/${provider}`, { method: "DELETE" }),
      putSmtp: (body) =>
        http<PlatformSettings["smtp"]>("/platform/settings/smtp", {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      deleteSmtp: () => http<void>("/platform/settings/smtp", { method: "DELETE" }),
      putStripe: (body) =>
        http<PlatformSettings["stripe"]>("/platform/settings/stripe", {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      putCloudflare: (body) =>
        http<PlatformSettings["cloudflare"]>("/platform/settings/cloudflare", {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      putGithub: (body) =>
        http<PlatformSettings["github"]>("/platform/settings/github", {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      putOrigins: (body) =>
        http<PlatformSettings["origins"]>("/platform/settings/origins", {
          method: "PUT",
          body: JSON.stringify(body),
        }),
    },
  },
};

/** Build a `?k=v&k=v` querystring from a PlatformQuery. Empty string when no
 * keys are set, so the URL stays bare. */
function platformQs(q?: PlatformQuery): string {
  if (!q) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

/* ---------- Factory ---------- */
export const services: Services = env.useMocks ? mockServices : httpServices;
